import { useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import { GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DashboardSection {
  id: string;
  title: string;
  component: ReactNode;
  colSpan: 1 | 2 | 3; // How many columns this section takes (1, 2, or 3)
  collapsed?: boolean;
}

interface DraggableDashboardProps {
  sections: DashboardSection[];
  onReorder: (sections: DashboardSection[]) => void;
  onToggleCollapse: (id: string) => void;
  onColSpanChange: (id: string, colSpan: 1 | 2 | 3) => void;
}

// Column widths for snap points (approximate based on 3-col grid)
const COL_WIDTHS = {
  1: 33.33,  // 1 column = ~33% of row
  2: 66.67,  // 2 columns = ~67% of row
  3: 100     // 3 columns = 100% of row (full width)
};

export function DraggableDashboard({ 
  sections, 
  onReorder, 
  onToggleCollapse,
  onColSpanChange
}: DraggableDashboardProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [resizePreview, setResizePreview] = useState<1 | 2 | 3 | null>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartColSpan = useRef<1 | 2 | 3>(1);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const contentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [contentHeights, setContentHeights] = useState<Record<string, number>>({});

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(entries => {
      setContentHeights(prev => {
        const next = { ...prev };
        let changed = false;

        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.sectionId;
          if (!id) continue;

          const measuredHeight = Math.ceil(entry.target.scrollHeight);
          if (next[id] !== measuredHeight) {
            next[id] = measuredHeight;
            changed = true;
          }
        }

        return changed ? next : prev;
      });
    });

    sections.forEach(section => {
      const content = contentRefs.current.get(section.id);
      if (!content) return;
      observer.observe(content);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) {
      setDragOverId(id);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');

    if (sourceId === targetId) return;

    const sourceIndex = sections.findIndex(s => s.id === sourceId);
    const targetIndex = sections.findIndex(s => s.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    // Reorder sections
    const newSections = [...sections];
    const [removed] = newSections.splice(sourceIndex, 1);
    const insertIndex = targetIndex > sourceIndex ? targetIndex - 1 : targetIndex;
    newSections.splice(insertIndex, 0, removed);

    // Now recalculate rows and auto-resize as needed
    const finalSections: DashboardSection[] = [];
    let currentRowSpan = 0;

    newSections.forEach(section => {
      const sectionSpan = section.colSpan;

      // Full width always starts new row
      if (sectionSpan === 3) {
        finalSections.push(section);
        currentRowSpan = 0;
        return;
      }

      // Check if this would overflow the row
      if (currentRowSpan + sectionSpan > 3) {
        // Row is full, need to either:
        // 1. Start new row (keep size)
        // 2. Shrink to fit remaining space
        const remainingSpace = 3 - currentRowSpan;
        
        if (remainingSpace > 0 && sectionSpan > remainingSpace) {
          // Resize to fit remaining space
          finalSections.push({ ...section, colSpan: remainingSpace as 1 | 2 });
          currentRowSpan = 3; // Row is now full
        } else {
          // Start new row
          finalSections.push(section);
          currentRowSpan = sectionSpan;
        }
      } else {
        // Fits in current row
        finalSections.push(section);
        currentRowSpan += sectionSpan;
      }
    });

    onReorder(finalSections);
    setDragOverId(null);
  }, [sections, onReorder]);

  // Resize handlers - drag from corner to resize
  const handleResizeStart = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    resizeStartX.current = e.clientX;
    resizeStartColSpan.current = section.colSpan;
    setResizingId(sectionId);
    setResizePreview(section.colSpan);

    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'se-resize';
  }, [sections]);

  useEffect(() => {
    if (!resizingId) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartX.current;
      const element = sectionRefs.current.get(resizingId);
      
      if (!element) return;
      
      const containerWidth = element.parentElement?.offsetWidth || element.offsetWidth;
      const currentWidthPercent = (COL_WIDTHS[resizeStartColSpan.current] / 100) * containerWidth;
      const newWidth = currentWidthPercent + deltaX;
      const newWidthPercent = (newWidth / containerWidth) * 100;

      // Determine which colSpan based on width percentage
      let preview: 1 | 2 | 3;
      if (newWidthPercent < 50) {
        preview = 1;
      } else if (newWidthPercent < 83) {
        preview = 2;
      } else {
        preview = 3;
      }
      
      setResizePreview(preview);
    };

    const handleMouseUp = () => {
      if (resizePreview && resizePreview !== resizeStartColSpan.current) {
        onColSpanChange(resizingId, resizePreview);
      }
      
      setResizingId(null);
      setResizePreview(null);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingId, resizePreview, onColSpanChange]);

  // Check if a row contains only requests + waterfall (in any order)
  const isRequestsWaterfallRow = (row: DashboardSection[]) => {
    if (row.length !== 2) return false;
    const ids = row.map(s => s.id).sort();
    return ids[0] === 'requests' && ids[1] === 'waterfall';
  };

  // Group sections into rows based on colSpan (max 3 columns per row)
  const rows: DashboardSection[][] = [];
  let currentRow: DashboardSection[] = [];
  let currentRowSpan = 0;

  sections.forEach(section => {
    const sectionSpan = section.colSpan;

    // Full width sections (colSpan=3) always start a new row
    if (sectionSpan === 3) {
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
        currentRowSpan = 0;
      }
      rows.push([section]);
      return;
    }

    // Check if adding this section would create a requests+waterfall pair
    const wouldBeRequestsWaterfall = currentRow.length === 1 && 
      ((currentRow[0].id === 'requests' && section.id === 'waterfall') ||
       (currentRow[0].id === 'waterfall' && section.id === 'requests'));

    if (wouldBeRequestsWaterfall) {
      // Add to current row to create the special pair
      currentRow.push(section);
      rows.push(currentRow);
      currentRow = [];
      currentRowSpan = 0;
    } else if (currentRowSpan + sectionSpan > 3) {
      // Row would exceed 3 columns, start new row
      if (currentRow.length > 0) {
        rows.push(currentRow);
      }
      currentRow = [section];
      currentRowSpan = sectionSpan;
    } else {
      // Add to current row
      currentRow.push(section);
      currentRowSpan += sectionSpan;
    }
  });

  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  // Get grid column class based on row content
  const getGridColsClass = (row: DashboardSection[]) => {
    // Special case: requests + waterfall together get 2-column layout
    if (isRequestsWaterfallRow(row)) {
      return 'grid-cols-1 lg:grid-cols-2';
    }
    
    // All other rows use 3-column layout
    const totalSpan = row.reduce((sum, s) => sum + s.colSpan, 0);
    if (totalSpan <= 1) return 'grid-cols-1 lg:grid-cols-3';
    if (totalSpan <= 2) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-3 lg:grid-cols-3';
  };

  // Get column span class for individual sections
  const getSectionColSpanClass = (section: DashboardSection, row: DashboardSection[]) => {
    // Special case: requests + waterfall get equal sizing in 2-col layout
    if (isRequestsWaterfallRow(row)) {
      return 'lg:col-span-1';
    }
    
    // If this section is being resized, show preview
    if (resizingId === section.id && resizePreview) {
      if (resizePreview === 3) return 'lg:col-span-3';
      if (resizePreview === 2) return 'md:col-span-2 lg:col-span-2';
      return 'col-span-1';
    }
    
    // Normal case: use colSpan
    if (section.colSpan === 3) return 'lg:col-span-3';
    if (section.colSpan === 2) return 'md:col-span-2 lg:col-span-2';
    return 'col-span-1';
  };

  return (
    <div className="space-y-4">
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={cn(
            'grid gap-4',
            getGridColsClass(row)
          )}
        >
          {row.map(section => (
            <div
              key={section.id}
              ref={(el) => {
                if (el) sectionRefs.current.set(section.id, el);
              }}
              draggable={!section.collapsed && !resizingId}
              onDragStart={(e) => handleDragStart(e, section.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, section.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, section.id)}
                className={cn(
                  'relative group transition-all duration-200',
                  getSectionColSpanClass(section, row),
                  dragOverId === section.id && 'ring-2 ring-brand-500 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-950 rounded-lg',
                  draggedId === section.id && 'opacity-50',
                  resizingId === section.id && 'z-50',
                  section.collapsed && 'self-start'
                )}
              >
              {/* Drag handle */}
              <div className="absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10 p-1">
                <GripVertical className="w-4 h-4 text-slate-400 dark:text-slate-600" />
              </div>
              
              {/* Section with header and collapse/expand functionality */}
              <div className={cn(
                "card overflow-hidden flex flex-col",
                !section.collapsed && "h-full",
                resizingId === section.id && "ring-2 ring-brand-500"
              )}>
                {/* Section Header */}
                <button
                  type="button"
                  onClick={() => onToggleCollapse(section.id)}
                  className="w-full flex items-center justify-between p-4 sticky top-0 z-10 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
                  title={section.collapsed ? 'Expand section' : 'Collapse section'}
                  aria-expanded={!section.collapsed}
                  aria-label={section.collapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
                >
                  <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {section.title}
                  </h2>
                  <span className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                    {section.collapsed ? (
                      <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-600" />
                    ) : (
                      <ChevronUp className="w-4 h-4 text-slate-400 dark:text-slate-600" />
                    )}
                  </span>
                </button>
                
                {/* Section Content */}
                <div
                  className={cn(
                    'transition-[max-height,opacity] duration-300 ease-in-out overflow-hidden',
                    section.collapsed ? 'max-h-0 opacity-0' : 'opacity-100'
                  )}
                  style={{
                    maxHeight: section.collapsed
                      ? '0px'
                      : `${contentHeights[section.id] ?? 2000}px`,
                  }}
                >
                  <div
                    data-section-id={section.id}
                    className="px-5 pb-5 pt-2"
                    ref={(el) => {
                      if (el) {
                        contentRefs.current.set(section.id, el);
                      } else {
                        contentRefs.current.delete(section.id);
                      }
                    }}
                  >
                    {section.component}
                  </div>
                </div>
              </div>
              
              {/* Draggable resize handle - bottom-right corner */}
              {!section.collapsed && (
                <div
                  onMouseDown={(e) => handleResizeStart(e, section.id)}
                  className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity z-30 hover:scale-110"
                  title="Drag to resize (1, 2, or 3 columns)"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="absolute bottom-1 right-1 text-slate-500 dark:text-slate-500"
                  >
                    <path
                      d="M4 10L10 4M8 12L12 8M1 13L13 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
              
              {/* Resize preview indicator */}
              {resizingId === section.id && resizePreview && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-xs px-2 py-1 rounded-md shadow-lg z-50 whitespace-nowrap">
                  {resizePreview} column{resizePreview > 1 ? 's' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
